import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useQueryParam from "useQueryParams";
import BillingOverview from "./Overview";
import PaymentAndCredits from "./PaymentAndCredits";
import Payments from "./Payments";
import UsageSummary from "./UsageSummaries/UsageSummary";
import { PlusIcon } from "@heroicons/react/24/outline";
import { AddOrgModal } from "components/Modals/AddOrgModel";
import { Button } from "components/Graphs/Button";
import {
  formatProgramEndDate,
  getProgramDaysRemaining,
  shouldShowAcademicGrantAccess,
} from "components/utils";

interface LocationState {
  segment?: string;
}

interface CreditsBillingPageProps {
  embedded?: boolean;
}

const CreditsBillingPage: React.FC<CreditsBillingPageProps> = ({
  embedded = false,
}) => {
  const { state } = useLocation<LocationState>();
  const orgContext = useContext(OrgContext);
  const instanceTypesContext = useContext(InstanceTypesContext);
  const billingRef = React.useRef<null | HTMLDivElement>(null);
  const [withCoupon] = useQueryParam("coupon", "");
  const [openAddCredits] = useQueryParam("openAddCredits", "false");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    document.title = "Organization Billing | Brev.dev";
    if (state?.segment === "billing") {
      billingRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [state?.segment]);

  if (orgContext.activeOrg) {
    return (
      <>
        <div className={embedded ? "flex flex-col" : "flex flex-col mt-8"}>
          <BillingOverview
            activeOrgId={orgContext.activeOrg.id}
            currencyDisplay={instanceTypesContext.currencyDisplay}
            showProgramEndDate={shouldShowAcademicGrantAccess(
              orgContext.activeOrg,
              instanceTypesContext.currencyDisplay,
            )}
            programEndDateLabel={formatProgramEndDate(orgContext.activeOrg)}
            programDaysRemaining={getProgramDaysRemaining(orgContext.activeOrg)}
          />
        </div>
        <div className="relative">
          <div
            className={
              instanceTypesContext.currencyDisplay === "credits"
                ? ""
                : "opacity-20"
            }
          >
            <div className="flex flex-row mt-8">
              <div className="flex flex-col w-full">
                <PaymentAndCredits
                  withCoupon={withCoupon}
                  openAddCredits={openAddCredits === "true"}
                />
              </div>
            </div>
            <div className="mt-8">
              <Payments activeOrgId={orgContext.activeOrgId} />
            </div>
          </div>

          {instanceTypesContext.currencyDisplay !== "credits" && (
            <div className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center pointer-events-auto">
              <div className="flex flex-col gap-2 w-1/3">
                <div className="text-white text-center">
                  Create a new organization to purchase credits and unlock
                  additional features
                </div>
                <Button onClick={() => setShowModal(true)}>
                  <PlusIcon className="h-5 w-5" />
                  <span className="text-xs">Create a new organization</span>
                </Button>
              </div>
              <AddOrgModal
                isOpen={showModal}
                setOpen={setShowModal}
                onSuccess={() => {
                  setShowModal(false);
                }}
                onCancel={() => {
                  setShowModal(false);
                }}
              />
            </div>
          )}
        </div>
        <div className="mt-8">
          <UsageSummary
            activeOrgId={orgContext.activeOrgId}
            currencyDisplay={instanceTypesContext.currencyDisplay}
            showRuntimeDays={shouldShowAcademicGrantAccess(
              orgContext.activeOrg,
              instanceTypesContext.currencyDisplay,
            )}
          />
        </div>
      </>
    );
  }
  // when clicking the settings button, response time is so fast the seeing loading bars feels off
  return <div />;
};

export default CreditsBillingPage;
